import { useEffect, useState } from 'react';
import { NumberInput, TimeUnitsDropdown, DefaultInputPropTypes, useFormContext, useInputHelper } from 'framework/form';
import { useDidUpdateEffect } from 'framework/utils';
import useAssetService from 'domain/finance/assets/useAssetService';
import { MultiInputWrapperLayout } from 'framework/ui/layout';

const DEFAULT_FUNCTION = () => {};

type UsefulLifePeriodValue = {
  amount?: number | null;
  timeUnit?: string | null;
};

type AssetUsefulLifeBusinessRulesMetadataDto = {
  name: string;
  overrides: string[];
  defaultValue?: UsefulLifePeriodValue | null;
};

type AssetUsefulLifeInputProps = DefaultInputPropTypes<any> & {
  categorySource: string;
  typeSource: string;
  isEditForm?: boolean;
};

const AssetUsefulLifeInput = ({
  source,
  label,
  categorySource,
  typeSource,
  disabled,
  isEditForm,
  ...props
}: AssetUsefulLifeInputProps) => {
  const { getBusinessRule } = useAssetService();
  const { getValue: getCategory } = useInputHelper({ source: categorySource });
  const { getValue: getType } = useInputHelper({ source: typeSource });
  const { getValue: getName } = useInputHelper<string>({ source: 'name' });
  const { getValue: getPurchaseDate } = useInputHelper<string>({ source: 'purchaseDate' });
  const { getRawOnChangeCallback, getValue: getCurrentUsefulLife } = useInputHelper<UsefulLifePeriodValue>({ source });

  const [businessRule, setBusinessRule] = useState<AssetUsefulLifeBusinessRulesMetadataDto | null>(null);
  const [loading, setLoading] = useState(false);

  const { setSubmitEnabled = DEFAULT_FUNCTION, setFormErrors = DEFAULT_FUNCTION, formErrors = {} } = useFormContext();

  const category = getCategory();
  const type = getType();
  const name = getName();
  const purchaseDate = getPurchaseDate();
  const rawOnChangeCallback = getRawOnChangeCallback();

  useEffect(() => {
    if (!category || !type || !name || !purchaseDate) {
      setBusinessRule(null);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setSubmitEnabled(false);

        const r = await getBusinessRule({
          category,
          type,
          name,
          purchaseDate,
        });

        if (!cancelled) {
          setBusinessRule(r ?? null);
        }
      } catch (e) {
        if (!cancelled) {
          setBusinessRule(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setSubmitEnabled(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, type, name, purchaseDate, isEditForm]);

  useDidUpdateEffect(() => {
    if (!rawOnChangeCallback) return;

    const currentValue = getCurrentUsefulLife();
    const hasAnyValue = !!currentValue && (currentValue.amount != null || !!currentValue.timeUnit);

    if (businessRule?.defaultValue) {
      rawOnChangeCallback({
        [source]: businessRule.defaultValue,
      });
    } else if (!isEditForm && !hasAnyValue) {
      rawOnChangeCallback({
        [source]: { timeUnit: 'MONTHS' },
      });
    }

    setFormErrors({
      ...formErrors,
      [`${source}.amount`]: [],
      [`${source}.timeUnit`]: [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessRule]);

  const canBeOverridden = !businessRule?.overrides?.includes('CUSTOM_USEFUL_LIFE');
  const disabledField = disabled || loading || (!!businessRule?.defaultValue && !canBeOverridden);

  return (
    <MultiInputWrapperLayout
      label={label as any}
      inputSources={[`${source}.amount`, `${source}.timeUnit`]}
      gridTemplateColumns="1fr 130px"
      isRequired
    >
      <NumberInput source={`${source}.amount`} {...props} disabled={disabledField} />
      <TimeUnitsDropdown source={`${source}.timeUnit`} excludedUnits={['DAYS', 'WEEKS']} disabled={disabledField} />
    </MultiInputWrapperLayout>
  );
};

export default AssetUsefulLifeInput;
